import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import { 
    RefreshCw, Trash2, Mail, Phone, Calendar, 
    Laptop, CheckCircle, Clock, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminDemoRequests = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const [allMessages, setAllMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchMessages = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}contacts.php`);
            const data = await res.json();
            if (Array.isArray(data)) {
                setAllMessages(data);
            }
        } catch {
            toast.error(t('admin.messages.toastLoadError') || 'მოთხოვნების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user && isSuperAdmin()) {
            fetchMessages();
        }
    }, [user, isSuperAdmin, fetchMessages]);

    // Check if message is a demo request or product inquiry
    const isDemoRequest = (msg) => {
        if (!msg) return false;
        if (msg.request_type === 'demo' || msg.product) return true;
        const text = (msg.message || '').toLowerCase();
        return text.includes('დემო') || 
               text.includes('demo') || 
               text.includes('შეთავაზებ') || 
               text.includes('quote') || 
               text.includes('პროდუქტ') ||
               text.includes('citizen') ||
               text.includes('portal');
    };

    // Extract product name from message if present
    const extractProduct = (msg) => {
        if (msg.product) return msg.product;
        const text = msg.message || '';
        const match = text.match(/\[დემო \/ შეთავაზების მოთხოვნა:\s*([^\]]+)\]/i) || 
                      text.match(/\[Demo Request:\s*([^\]]+)\]/i);
        if (match && match[1]) return match[1].trim();
        return null;
    };

    // Filter to demo requests (or all if filter set)
    const demoRequests = allMessages.filter(isDemoRequest);

    // Apply UI filters
    const filteredList = (demoRequests.length > 0 ? demoRequests : allMessages).filter(msg => {
        const matchStatus = statusFilter === 'all' || msg.status === statusFilter;
        const term = searchTerm.toLowerCase();
        const matchSearch = !term || 
            (msg.name || '').toLowerCase().includes(term) ||
            (msg.email || '').toLowerCase().includes(term) ||
            (msg.phone || '').toLowerCase().includes(term) ||
            (msg.message || '').toLowerCase().includes(term);
        return matchStatus && matchSearch;
    });

    const handleStatusChange = async (id, newStatus) => {
        try {
            const sanitized = sanitizePayload({ status: newStatus });
            const res = await fetchWithSecurity(`${API_URL}contacts.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('სტატუსი განახლდა');
                setAllMessages(prev => prev.map(m => m.id === id ? { ...m, status: newStatus } : m));
            } else {
                toast.error(data.error || 'სტატუსის განახლება ვერ მოხერხდა');
            }
        } catch {
            toast.error('სტატუსის განახლება ვერ მოხერხდა');
        }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ მოთხოვნის წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}contacts.php?id=${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success('მოთხოვნა წაიშალა');
                setAllMessages(prev => prev.filter(m => m.id !== id));
            } else {
                toast.error(data.error || 'წაშლა ვერ მოხერხდა');
            }
        } catch {
            toast.error('წაშლა ვერ მოხერხდა');
        }
    };

    if (authLoading || !user) return null;

    const newCount = (demoRequests.length > 0 ? demoRequests : allMessages).filter(m => m.status === 'New').length;
    const repliedCount = (demoRequests.length > 0 ? demoRequests : allMessages).filter(m => m.status === 'Replied').length;

    const getStatusBadge = (status) => {
        switch (status) {
            case 'New': return { label: 'ახალი', cls: 'shadcn-badge-danger' };
            case 'Read': return { label: 'წაკითხული', cls: 'shadcn-badge-info' };
            case 'Replied': return { label: 'პასუხგაცემული', cls: 'shadcn-badge-success' };
            case 'Archived': return { label: 'დაარქივებული', cls: 'shadcn-badge-secondary' };
            default: return { label: status, cls: 'shadcn-badge-secondary' };
        }
    };

    return (
        <AdminLayout title="დემო & შეთავაზების მოთხოვნები">
            {/* ── TOP STATS ── */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="shadcn-card p-3 d-flex align-items-center gap-3">
                        <div className="p-3 rounded-3 bg-primary bg-opacity-10 text-primary">
                            <Laptop size={24} />
                        </div>
                        <div>
                            <span className="text-muted small d-block">სულ დემო მოთხოვნები</span>
                            <span className="fs-4 fw-bold text-dark">{demoRequests.length}</span>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="shadcn-card p-3 d-flex align-items-center gap-3">
                        <div className="p-3 rounded-3 bg-danger bg-opacity-10 text-danger">
                            <Clock size={24} />
                        </div>
                        <div>
                            <span className="text-muted small d-block">ახალი / მოლოდინში</span>
                            <span className="fs-4 fw-bold text-danger">{newCount}</span>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="shadcn-card p-3 d-flex align-items-center gap-3">
                        <div className="p-3 rounded-3 bg-success bg-opacity-10 text-success">
                            <CheckCircle size={24} />
                        </div>
                        <div>
                            <span className="text-muted small d-block">ჩანიშნული / პასუხგაცემული</span>
                            <span className="fs-4 fw-bold text-success">{repliedCount}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MAIN CARD ── */}
            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div className="shadcn-card-title">
                        <Laptop size={20} className="text-primary" />
                        <span>დემო & შეთავაზების მოთხოვნები ({filteredList.length})</span>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Search input */}
                        <div className="position-relative" style={{ minWidth: '220px' }}>
                            <Search size={14} className="position-absolute text-muted" style={{ left: '10px', top: '10px' }} />
                            <input 
                                type="text"
                                className="shadcn-input ps-4 py-1 small"
                                placeholder="ძებნა (კლიენტი, პროდუქტი...)"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Status Filter */}
                        <select 
                            className="shadcn-select form-select-sm py-1"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ width: 'auto' }}
                        >
                            <option value="all">ყველა სტატუსი</option>
                            <option value="New">ახალი</option>
                            <option value="Read">წაკითხული</option>
                            <option value="Replied">პასუხგაცემული</option>
                            <option value="Archived">დაარქივებული</option>
                        </select>

                        <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchMessages}>
                            <RefreshCw size={14} /> განახლება
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>მოთხოვნები იტვირთება...</span>
                    </div>
                ) : filteredList.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <Laptop size={36} className="text-muted opacity-50 mb-2 d-block mx-auto" />
                        <p className="mb-0">დემო ჩვენების მოთხოვნები ჯერ არ არის მიღებული.</p>
                        <small className="text-muted">როდესაც მომხმარებელი პროდუქტის გვერდიდან მოითხოვს დემოს, მონაცემები აქ გამოჩნდება.</small>
                    </div>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>თარიღი</th>
                                    <th>მოთხოვნილი პროდუქტი</th>
                                    <th>დამკვეთი / კლიენტი</th>
                                    <th>საკონტაქტო ინფო</th>
                                    <th>მოთხოვნის დეტალები</th>
                                    <th>სტატუსი</th>
                                    <th>მოქმედებები</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map(msg => {
                                    const product = extractProduct(msg);
                                    const st = getStatusBadge(msg.status);

                                    return (
                                        <tr key={msg.id}>
                                            <td>
                                                <div className="fw-semibold text-dark d-flex align-items-center gap-1">
                                                    <Calendar size={13} className="text-muted" />
                                                    <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                                                </div>
                                                <div className="smallest text-muted">
                                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </td>

                                            <td>
                                                {product ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 fs-7">
                                                            📦 {product}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="badge bg-secondary bg-opacity-10 text-dark px-2 py-1 fs-7">
                                                        ზოგადი შეთავაზება
                                                    </span>
                                                )}
                                            </td>

                                            <td className="fw-bold text-dark">{msg.name}</td>

                                            <td>
                                                <div className="d-flex align-items-center gap-1 text-dark small mb-1">
                                                    <Mail size={13} className="text-primary" />
                                                    <a href={`mailto:${msg.email}`} className="text-dark text-decoration-none">
                                                        {msg.email}
                                                    </a>
                                                </div>
                                                {msg.phone && (
                                                    <div className="d-flex align-items-center gap-1 text-muted smallest">
                                                        <Phone size={11} />
                                                        <a href={`tel:${msg.phone}`} className="text-muted text-decoration-none">
                                                            {msg.phone}
                                                        </a>
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                <div className="text-dark small" style={{ maxWidth: '320px', maxHeight: '90px', overflowY: 'auto' }}>
                                                    {msg.message}
                                                </div>
                                            </td>

                                            <td>
                                                <select
                                                    className={`shadcn-select form-select-sm py-1 ${st.cls}`}
                                                    value={msg.status}
                                                    onChange={(e) => handleStatusChange(msg.id, e.target.value)}
                                                    style={{ maxWidth: '140px' }}
                                                >
                                                    <option value="New">ახალი</option>
                                                    <option value="Read">წაკითხული</option>
                                                    <option value="Replied">პასუხგაცემული</option>
                                                    <option value="Archived">დაარქივებული</option>
                                                </select>
                                            </td>

                                            <td>
                                                <div className="d-flex align-items-center gap-1">
                                                    <a 
                                                        href={`mailto:${msg.email}?subject=Orbitronix%20Demo%20Presentation%20-%20${encodeURIComponent(product || 'Product')}`}
                                                        className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary"
                                                        title="ელ-ფოსტით პასუხი"
                                                    >
                                                        <Mail size={13} />
                                                    </a>
                                                    <button 
                                                        className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" 
                                                        onClick={() => deleteMessage(msg.id)}
                                                        title="წაშლა"
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminDemoRequests;
